/**
 * API client for the declared data elements
 * (apps/api-controller/src/modules/discovery/declared-elements/handlers.ts):
 * the elements an approved FOCTTA Consent DPIA sent, and those entered in
 * Discovery. GET /v1/discovery/declared-elements needs discovery:read; the
 * table's row-level security limits a unit-scoped user to their units' rows
 * and the tenant-wide ones, so the page shows what the API returns.
 */

import { apiFetch } from './api';

export type DeclaredSource = 'core_dpia' | 'manual';
export type DeclaredMappingStatus = 'mapped' | 'keyword_rule' | 'unmapped';
export type DeclaredStatus = 'active' | 'retired';
export type ScanReason = 'retired' | 'unmapped' | 'unit_not_synced' | 'over_cap' | 'rule_disabled' | 'label_switched_off';

export interface DeclaredScanState {
  inEffect: boolean;
  reasons: ScanReason[];
  keywordsInEffect: string[];
  keywordsOverCap: string[];
  keywordsDisabled: string[];
  labelSwitchedOffInUnits: string[];
}

export interface DeclaredElement {
  id: string;
  businessUnitId: string | null;
  /** The Consent unit the DPIA names; set with no businessUnitId while that unit has not synced here. */
  coreBusinessUnitRef: string | null;
  source: DeclaredSource;
  /** The Consent assessment id for a DPIA's element. */
  sourceRef: string | null;
  /** The DPIA's reference as Consent shows it, e.g. DPIA-2026-017. */
  sourceReference: string | null;
  sourceVersion: number | null;
  elementKey: string;
  name: string;
  category: string | null;
  piiClass: string | null;
  sensitive: boolean;
  mappingStatus: DeclaredMappingStatus;
  resolvedLabel: string | null;
  ruleLabel: string | null;
  ruleKeywords: string[];
  status: DeclaredStatus;
  retiredReason: string | null;
  createdAt: string;
  updatedAt: string;
  retiredAt: string | null;
  scan: DeclaredScanState;
}

/** The route's own ceiling per request (MAX_ELEMENTS_PER_EVENT). */
const PAGE = 2000;
/** 20 000 elements is far past any real inventory; stop rather than loop on a bad total. */
const MAX_PAGES = 10;

/** Every element with the given status, page by page. */
export async function listDeclaredElements(status: DeclaredStatus): Promise<DeclaredElement[]> {
  const out: DeclaredElement[] = [];
  for (let page = 0; page < MAX_PAGES; page++) {
    const params = new URLSearchParams({ status, limit: String(PAGE), offset: String(page * PAGE) });
    const res = await apiFetch<{ items: DeclaredElement[]; total: number }>(`/v1/discovery/declared-elements?${params}`);
    out.push(...res.items);
    if (res.items.length < PAGE || out.length >= res.total) break;
  }
  return out;
}
